import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, UserPlus, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import CreateCustomerSheet from "@/components/admin/CreateCustomerSheet";


type ProfileRow = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  phone: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  vitamin_stations: number;
};

type SubRow = {
  user_id: string;
  status: string;
  tier_tubes: number;
  nickname: string | null;
  is_pilot: boolean;
};

const statusVariant = (s: ProfileRow["status"]) => {
  switch (s) {
    case "approved":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    case "pending":
      return "bg-amber-100 text-amber-800 hover:bg-amber-100";
    case "rejected":
      return "bg-rose-100 text-rose-800 hover:bg-rose-100";
  }
};

const subStatusVariant = (s: string) => {
  switch (s) {
    case "active":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    case "paused":
      return "bg-amber-100 text-amber-800 hover:bg-amber-100";
    case "cancelled":
      return "bg-rose-100 text-rose-800 hover:bg-rose-100";
    default:
      return "bg-slate-100 text-slate-800 hover:bg-slate-100";
  }
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });

const AdminCustomersPage = () => {
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [subs, setSubs] = useState<Record<string, SubRow[]>>({});
  const [inviteStatus, setInviteStatus] = useState<Record<string, { invited_at: string | null; last_sign_in_at: string | null }>>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected" | "pilot">("all");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [invitingId, setInvitingId] = useState<string | null>(null);

  const sendInvite = async (userId: string, email: string) => {
    setInvitingId(userId);
    try {
      const { data, error } = await supabase.functions.invoke("admin-send-customer-invite", {
        body: { user_id: userId },
      });
      if (error) {
        const ctx = (error as { context?: { error?: string } }).context;
        throw new Error(ctx?.error || error.message || "Kunde inte skicka inbjudan");
      }
      if (data && typeof data === "object" && "error" in data && data.error) {
        throw new Error(String((data as { error: string }).error));
      }
      const isRecovery = (data as { mode?: string } | null)?.mode === "recovery";
      toast.success(isRecovery ? "Återställningsmejl skickat" : "Inbjudan skickad", {
        description: isRecovery
          ? `Kunden hade redan ett konto — en länk för nytt lösenord skickades till ${email}.`
          : `Mejl skickat till ${email}.`,
      });
      await load();
    } catch (err) {
      toast.error("Kunde inte skicka inbjudan", {
        description: err instanceof Error ? err.message : "Något gick fel",
      });
    } finally {
      setInvitingId(null);
    }
  };



  const load = async () => {
    setLoading(true);
    const [{ data: ps }, { data: ss }, { data: inv }] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, email, first_name, last_name, company, phone, status, created_at, vitamin_stations")
        .order("created_at", { ascending: false }),
      supabase
        .from("subscriptions")
        .select("user_id, status, tier_tubes, nickname, is_pilot")
        .in("status", ["active", "paused"])
        .order("created_at", { ascending: true }),
      // Admin-only RPC validates the caller server-side before reading auth metadata.
      (supabase as any).rpc("get_admin_customer_invite_statuses"),
    ]);
    setProfiles((ps as ProfileRow[]) || []);
    const map: Record<string, SubRow[]> = {};
    ((ss as SubRow[]) || []).forEach((s) => {
      (map[s.user_id] ||= []).push(s);
    });
    setSubs(map);
    const im: Record<string, { invited_at: string | null; last_sign_in_at: string | null }> = {};
    ((inv as Array<{ user_id: string; invited_at: string | null; last_sign_in_at: string | null }>) || []).forEach((r) => {
      im[r.user_id] = { invited_at: r.invited_at, last_sign_in_at: r.last_sign_in_at };
    });
    setInviteStatus(im);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles.filter((p) => {
      if (filter === "pilot") {
        const userSubs = subs[p.id] || [];
        if (!userSubs.some((s) => s.is_pilot)) return false;
      } else if (filter !== "all" && p.status !== filter) {
        return false;
      }
      if (!q) return true;
      const name = `${p.first_name ?? ""} ${p.last_name ?? ""}`.toLowerCase();
      return (
        name.includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.company ?? "").toLowerCase().includes(q)
      );
    });
  }, [profiles, search, filter, subs]);

  const counts = useMemo(
    () => ({
      all: profiles.length,
      pending: profiles.filter((p) => p.status === "pending").length,
      approved: profiles.filter((p) => p.status === "approved").length,
      rejected: profiles.filter((p) => p.status === "rejected").length,
      pilot: profiles.filter((p) => (subs[p.id] || []).some((s) => s.is_pilot)).length,
    }),
    [profiles, subs],
  );

  return (
    <AdminLayout title="Kunder">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök namn, e-post eller företag…"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
              <TabsList>
                <TabsTrigger value="all">Alla ({counts.all})</TabsTrigger>
                <TabsTrigger value="pending">Väntar ({counts.pending})</TabsTrigger>
                <TabsTrigger value="approved">Godkända ({counts.approved})</TabsTrigger>
                <TabsTrigger value="rejected">Avvisade ({counts.rejected})</TabsTrigger>
                <TabsTrigger value="pilot">Pilotkunder ({counts.pilot})</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Skapa kundkonto
            </Button>
          </div>
        </div>

        <CreateCustomerSheet
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={load}
        />

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Namn</TableHead>
                <TableHead>Företag</TableHead>
                <TableHead className="text-center">Stationer</TableHead>
                <TableHead>E-post</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Prenumeration</TableHead>
                <TableHead>Registrerad</TableHead>
                <TableHead aria-label="Åtgärd" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">
                    Laddar…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">
                    Inga kunder matchar.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((p) => {
                  const sub = subs[p.id];
                  const name = [p.first_name, p.last_name].filter(Boolean).join(" ") || "—";
                  return (
                    <TableRow key={p.id} className="cursor-pointer">
                      <TableCell className="font-medium">
                        <Link to={`/admin/customers/${p.id}`} className="hover:underline">
                          {name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{p.company || "—"}</TableCell>
                      <TableCell className="text-center">
                        {p.vitamin_stations > 0 ? (
                          <Badge variant="outline" className="font-mono">{p.vitamin_stations}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{p.email}</span>
                          {inviteStatus[p.id] && inviteStatus[p.id].invited_at === null && inviteStatus[p.id].last_sign_in_at === null && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={invitingId === p.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                sendInvite(p.id, p.email);
                              }}
                              className="h-6 px-2 text-[10px] uppercase tracking-wider border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              {invitingId === p.id ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3 mr-1" />
                              )}
                              Inbjudan ej skickad
                            </Button>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge className={statusVariant(p.status)}>{p.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {sub && sub.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {sub.map((s, i) => (
                              <div key={i} className="flex items-center gap-2 flex-wrap">
                                <Badge className={subStatusVariant(s.status)}>{s.status}</Badge>
                                {s.is_pilot && (
                                  <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-[10px] uppercase tracking-wider">
                                    Pilot
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {s.tier_tubes} tuber
                                </span>
                                {s.nickname && (
                                  <span className="text-xs text-foreground italic">· {s.nickname}</span>
                                )}
                              </div>
                            ))}
                            {sub.length > 1 && (
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                                Totalt {sub.reduce((t, s) => t + s.tier_tubes, 0)} tuber · {sub.length} anläggningar
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {fmt(p.created_at)}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/admin/customers/${p.id}`}
                          className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                        >
                          Öppna <ArrowRight className="h-3 w-3" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCustomersPage;
